import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { apiUrl, fetchJson } from '../../apiBase';

const emptyObj = {};

// Idempotent endpoint; safe to de-dupe concurrent identical requests.
const _inflightPreview = new Map();

function _dedupePreview(key, fetcher) {
  if (!key) return fetcher();
  const existing = _inflightPreview.get(String(key));
  if (existing) return existing;
  const p = Promise.resolve()
    .then(fetcher)
    .finally(() => {
      if (_inflightPreview.get(String(key)) === p) {
        _inflightPreview.delete(String(key));
      }
    });
  _inflightPreview.set(String(key), p);
  return p;
}

const useLearningActivityAssessmentSession = ({
  schoolId,
  classId,
  learningActivityId,
  assessedOn,
  assessmentId,
  enabled = true,
  notifyError,
  notifySuccess,
}) => {
  const [sessionId, setSessionId] = useState(null);
  const [sessionStatus, setSessionStatus] = useState(null);

  const [students, setStudents] = useState([]);
  const [criteria, setCriteria] = useState([]);
  const [bands, setBands] = useState([]);
  const [results, setResults] = useState([]);

  // itemsByKey: { [`${studentId}:${criterionId}`]: { score } }
  const [itemsByKey, setItemsByKey] = useState(emptyObj);
  const [progress, setProgress] = useState(null);

  const [loadingSession, setLoadingSession] = useState(false);
  const [savingByKey, setSavingByKey] = useState(emptyObj);
  const [savingBulk, setSavingBulk] = useState(false);
  const readOnly = false;

  const resetSessionState = useCallback(() => {
    setSessionId(null);
    setSessionStatus(null);
    setStudents([]);
    setCriteria([]);
    setBands([]);
    setResults([]);
    setItemsByKey(emptyObj);
    setProgress(null);
    setSavingByKey(emptyObj);
    setSavingBulk(false);
  }, []);

  const lastKeyRef = useRef('');
  const reqIdRef = useRef(0);

  const savingAny = useMemo(
    () => savingBulk || (savingByKey && Object.keys(savingByKey).length > 0),
    [savingBulk, savingByKey]
  );

  const isSavingScore = useCallback(
    (studentId, criterionId) => {
      if (studentId == null || criterionId == null) return false;
      const k = `${studentId}:${criterionId}`;
      return !!savingByKey?.[String(k)];
    },
    [savingByKey]
  );

  const markSavingKeys = useCallback((keys, isSaving) => {
    const list = Array.isArray(keys) ? keys.filter(Boolean) : [];
    if (list.length === 0) return;

    setSavingByKey((prev) => {
      const next = { ...(prev || {}) };
      if (isSaving) {
        list.forEach((k) => {
          next[String(k)] = true;
        });
        return next;
      }

      list.forEach((k) => {
        delete next[String(k)];
      });
      return next;
    });
  }, []);

  const loadSession = useCallback(async (sid) => {
    if (!sid) return;
    setLoadingSession(true);
    try {
      const res = await fetchJson(apiUrl(`/admin/learning-activity-assessments/${encodeURIComponent(sid)}`));
      if (!res.ok) throw new Error('session');
      const data = await res.json();
      setSessionStatus(data?.status);
      setStudents(data?.students || []);
      setCriteria(data?.criteria || []);
      setBands(data?.bands || []);
      setResults(data?.results || []);

      const map = {};
      (data?.items || []).forEach((it) => {
        const k = `${it.student_id}:${it.criterion_id}`;
        map[String(k)] = { score: it.score };
      });
      setItemsByKey(map);
    } catch (e) {
      notifyError && notifyError('Oturum yüklenemedi');
    } finally {
      setLoadingSession(false);
    }
  }, [notifyError]);

  const saveScoresBulk = useCallback(async (items, { onSuccessMsg } = {}) => {
    if (!sessionId) return;
    const list = Array.isArray(items) ? items.filter(Boolean) : [];
    if (list.length === 0) return;

    const keys = list.map((it) => `${it.student_id}:${it.criterion_id}`);

    // Optimistic local update
    setItemsByKey((prev) => {
      const next = { ...(prev || {}) };
      list.forEach((it) => {
        const k = `${it.student_id}:${it.criterion_id}`;
        next[String(k)] = { ...(next[String(k)] || {}), score: it.score };
      });
      return next;
    });

    markSavingKeys(keys, true);
    if (list.length > 1) setSavingBulk(true);

    try {
      const res = await fetchJson(apiUrl(`/admin/learning-activity-assessments/${encodeURIComponent(sessionId)}/scores`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: list }),
      });
      if (!res.ok) throw new Error('save_bulk');
      if (onSuccessMsg) notifySuccess && notifySuccess(onSuccessMsg);
    } catch (e) {
      notifyError && notifyError('Kaydetme başarısız');
      loadSession(sessionId);
    } finally {
      markSavingKeys(keys, false);
      setSavingBulk(false);
    }
  }, [sessionId, markSavingKeys, notifyError, notifySuccess, loadSession]);

  // When sessionId is set, load session + progress
  useEffect(() => {
    if (!sessionId) return;
    loadSession(sessionId);
  }, [sessionId, loadSession]);

  // Auto-resolve/create a session when context is ready.
  useEffect(() => {
    const key = (() => {
      if (!enabled) return '';
      if (!schoolId || !classId || !learningActivityId || !assessedOn) return '';
      return [schoolId, classId, learningActivityId, assessedOn, assessmentId || ''].map(String).join('|');
    })();

    if (!key) {
      reqIdRef.current += 1; // invalidate any in-flight request
      resetSessionState();
      lastKeyRef.current = '';
      return;
    }

    // Avoid starting the same POST twice (common in React 18 dev StrictMode)
    if (lastKeyRef.current === key) return;
    lastKeyRef.current = key;

    const rid = ++reqIdRef.current;

    (async () => {
      setLoadingSession(true);
      try {
        const data = await _dedupePreview(key, async () => {
          const res = await fetchJson(apiUrl('/admin/learning-activity-assessments/preview'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              school_id: Number(schoolId),
              class_id: Number(classId),
              learning_activity_id: Number(learningActivityId),
              assessed_on: String(assessedOn),
              ...(assessmentId ? { assessment_id: Number(assessmentId) } : {}),
            }),
          });
          if (!res.ok) {
            let detail = 'Oturum hazırlanamadı';
            try { detail = await res.text(); } catch (_) {}
            throw new Error(detail);
          }
          return await res.json();
        });

        if (rid !== reqIdRef.current) return;
        const sid = data?.session_id;
        if (!sid) {
          throw new Error('Oturum hazırlanamadı');
        }

        // Preview endpoint already returns progress fields.
        setProgress(data || null);

        if (rid !== reqIdRef.current) return;

        setSessionId(Number(sid));
        setSessionStatus(data?.status || null);
      } catch (e) {
        if (rid !== reqIdRef.current) return;
        notifyError && notifyError(String(e?.message || 'Oturum hazırlanamadı'));
        resetSessionState();
      } finally {
        if (rid !== reqIdRef.current) return;
        setLoadingSession(false);
      }
    })();
  }, [enabled, schoolId, classId, learningActivityId, assessedOn, assessmentId, notifyError, resetSessionState]);

  const setScoreForStudentCriterion = useCallback(async (studentId, criterionId, score) => {
    if (!sessionId || !criterionId || !studentId) return;
    const k = `${studentId}:${criterionId}`;

    setItemsByKey((prev) => ({
      ...(prev || {}),
      [String(k)]: { ...(prev?.[String(k)] || {}), score },
    }));

    markSavingKeys([k], true);
    try {
      const res = await fetchJson(apiUrl(`/admin/learning-activity-assessments/${encodeURIComponent(sessionId)}/scores`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            student_id: Number(studentId),
            criterion_id: Number(criterionId),
            score: (score === null || score === undefined || score === '' ? null : Number(score)),
          }],
        }),
      });
      if (!res.ok) throw new Error('save');
    } catch (e) {
      notifyError && notifyError('Kaydetme başarısız');
      loadSession(sessionId);
    } finally {
      markSavingKeys([k], false);
    }
  }, [sessionId, notifyError, loadSession, markSavingKeys]);

  const fillUnsetScoresForCriterion = useCallback(async ({ criterionId, score }) => {
    if (!sessionId) return;
    if (!criterionId) {
      notifyError && notifyError('Ölçüt seçin');
      return;
    }
    if (score === null || score === undefined || score === '') {
      notifyError && notifyError('Puan seçin');
      return;
    }

    const criterionIdNum = Number(criterionId);
    const scoreNum = Number(score);
    if (!Number.isFinite(criterionIdNum) || !Number.isFinite(scoreNum)) return;

    const updates = (students || [])
      .filter((s) => s && s.id != null)
      .map((s) => {
        const k = `${s.id}:${criterionIdNum}`;
        const existing = itemsByKey?.[String(k)]?.score;
        const isUnset = existing === null || existing === undefined || existing === '';
        if (!isUnset) return null;
        return {
          student_id: Number(s.id),
          criterion_id: criterionIdNum,
          score: scoreNum,
        };
      })
      .filter(Boolean);

    if (updates.length === 0) return;
    await saveScoresBulk(updates, { onSuccessMsg: 'Boş kalanlar dolduruldu' });
  }, [sessionId, students, itemsByKey, notifyError, saveScoresBulk]);

  const applyScoreToAll = useCallback(async ({ criterionId, score }) => {
    if (!sessionId || savingBulk) return;
    if (!criterionId) {
      notifyError && notifyError('Ölçüt seçin');
      return;
    }
    if (score === null || score === undefined || score === '') {
      notifyError && notifyError('Puan seçin');
      return;
    }

    const criterionIdNum = Number(criterionId);
    const scoreNum = Number(score);

    const updates = (students || [])
      .filter((s) => s && s.id != null)
      .map((s) => ({
        student_id: Number(s.id),
        criterion_id: criterionIdNum,
        score: scoreNum,
      }));

    await saveScoresBulk(updates, { onSuccessMsg: 'Tüm öğrencilere uygulandı' });
  }, [sessionId, savingBulk, students, notifyError, saveScoresBulk]);

  return {
    sessionId,
    sessionStatus,
    readOnly,
    students,
    criteria,
    bands,
    results,
    itemsByKey,
    progress,
    loadingSession,
    saving: savingAny,
    savingBulk,
    isSavingScore,
    setScoreForStudentCriterion,
    applyScoreToAll,
    fillUnsetScoresForCriterion,
    resetSessionState,
  };
};

export default useLearningActivityAssessmentSession;
